/**
 * The window which writes a poll into a post.
 *
 * It writes text. Press the button, fill the window, and a tag appears in the
 * body of the post:
 *
 *     [poll="Ktorú mapu chcete hrať?" pick=2 until=2026-10-01 20:00]
 *     [*] Rovinatá
 *     [*] Hornatá
 *     [/poll]
 *
 * Nothing is created anywhere until the post itself is saved, which is the
 * whole reason it works this way. Somebody who opens the window and changes
 * their mind leaves nothing behind; a poll survives the post being quoted,
 * because it is part of what was quoted; and anybody who would rather type
 * the tag than fill a form may, because the form only ever produces what
 * they would have typed.
 *
 * The window is not a form. It stands inside the page which already holds
 * one, and a form inside a form is a form a browser silently drops - so the
 * buttons are ordinary buttons and nothing here submits anything.
 */

import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** How few options a poll may offer, matching what the server will accept. */
const FLOOR = 2;

/** How many it may offer. */
const CEILING = 12;

/** Return the quotes and brackets taken out, so a value cannot break the tag. */
function safe(value: string): string {
    return value.replace(/["\[\]]/g, "").replace(/\s+/g, " ").trim();
}

/** Return the tag which says what the window was filled in with. */
export function pollMarkup(question: string, options: string[], pick: number, until: string): string {
    const asked = safe(question);
    const offered = options.map(safe).filter((option, at, all) => option && all.indexOf(option) === at);

    if (!asked || offered.length < FLOOR) {
        return "";
    }

    const settings = [`"${asked}"`];

    // Only what was actually chosen is written. A tag carrying `pick=1` and an
    // empty `until` is a tag saying two things which are the default anyway,
    // and the point of it being text is that somebody can read it.
    if (pick > 1) {
        settings.push(`pick=${Math.min(pick, offered.length)}`);
    }

    if (until) {
        settings.push(`until=${until.replace("T", " ")}`);
    }

    const lines = offered.map((option) => `[*] ${option}`);

    return `[poll=${settings.join(" ")}]\n${lines.join("\n")}\n[/poll]\n`;
}

/** Wire the window of one editor. */
export function bindPollDialog(container: HTMLElement): void {
    const dialog = query<HTMLDialogElement>("[data-poll-dialog]");
    const open = query<HTMLElement>("[data-poll-open]", container);
    const field = query<HTMLTextAreaElement>("[data-editor-textarea]", container);

    if (!dialog || !open || !field) {
        return;
    }

    const list = query<HTMLElement>("[data-poll-options]", dialog);
    const question = query<HTMLInputElement>("[data-poll-question]", dialog);
    const pick = query<HTMLInputElement>("[data-poll-pick]", dialog);
    const until = query<HTMLInputElement>("[data-poll-until]", dialog);
    const insert = query<HTMLButtonElement>("[data-poll-insert]", dialog);

    if (!list || !question || !pick || !until || !insert) {
        return;
    }

    /** Return what has been typed into the option boxes. */
    const written = (): string[] =>
        queryAll<HTMLInputElement>("[data-poll-option]", list)
            .map((box) => box.value.trim())
            .filter(Boolean);

    /** Say whether there is enough to write a poll, and what may be picked. */
    const settle = (): void => {
        const options = written();

        insert.disabled = !question.value.trim() || options.length < FLOOR;
        // Never more than there are: a poll offering to pick five of three
        // asks nothing at all.
        pick.max = String(Math.max(1, options.length));

        if (Number(pick.value) > options.length && options.length) {
            pick.value = String(options.length);
        }
    };

    /** Put one more empty option at the end of the list. */
    const addOption = (value = ""): void => {
        if (queryAll("[data-poll-option]", list).length >= CEILING) {
            return;
        }

        const row = document.createElement("div");
        row.className = "poll-editor__row";

        const box = document.createElement("input");
        box.type = "text";
        box.maxLength = 120;
        box.dataset["pollOption"] = "";
        box.placeholder = text("js.poll.option", "Možnosť");
        box.value = value;
        box.addEventListener("input", settle);

        const drop = document.createElement("button");
        drop.type = "button";
        drop.className = "poll-editor__drop";
        drop.setAttribute("aria-label", text("js.poll.drop", "Odobrať možnosť"));
        drop.innerHTML =
            '<svg class="icon" width="12" height="12" aria-hidden="true">' +
            '<use href="/static/assets/icons/icons.svg#close"></use></svg>';

        drop.addEventListener("click", () => {
            // Never below the floor: a poll with one option is a statement
            // with a button under it.
            if (queryAll("[data-poll-option]", list).length > FLOOR) {
                row.remove();
                settle();
            }
        });

        row.append(box, drop);
        list.appendChild(row);
        settle();
    };

    /** Empty the window, so it never opens holding the last poll. */
    const reset = (): void => {
        question.value = "";
        pick.value = "1";
        until.value = "";
        list.replaceChildren();

        for (let index = 0; index < FLOOR; index += 1) {
            addOption();
        }
    };

    open.addEventListener("click", () => {
        reset();
        dialog.showModal();
        question.focus();
    });

    question.addEventListener("input", settle);
    query<HTMLElement>("[data-poll-add]", dialog)?.addEventListener("click", () => addOption());
    query<HTMLElement>("[data-poll-cancel]", dialog)?.addEventListener("click", () => dialog.close());

    insert.addEventListener("click", () => {
        const markup = pollMarkup(question.value, written(), Number(pick.value) || 1, until.value);
        if (!markup) {
            return;
        }

        // Written where the cursor is, like every other button of the
        // toolbar, and the cursor lands after it so typing carries on below.
        const at = field.selectionStart ?? field.value.length;
        const before = field.value.slice(0, at);
        const after = field.value.slice(field.selectionEnd ?? at);
        const lead = before && !before.endsWith("\n") ? "\n" : "";

        field.value = `${before}${lead}${markup}${after}`;
        field.focus();
        field.selectionStart = field.selectionEnd = before.length + lead.length + markup.length;
        field.dispatchEvent(new Event("input", { bubbles: true }));

        dialog.close();
    });
}

/** Wire every editor of the current page which offers a poll. */
export function initPollDialogs(): void {
    for (const container of queryAll<HTMLElement>("[data-editor]")) {
        bindPollDialog(container);
    }
}
